/**
 * Admin Order Statistics Widget
 *
 * Displays order statistics for the admin dashboard:
 * - Total Orders
 * - Pending Orders (with attention badge)
 * - Total Revenue
 * - Failed Orders (with warning)
 * - Click to navigate to orders page with filters
 */

import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Clock,
  DollarSign,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrderStatistics } from "@/hooks/useAdminOrders";
import { formatCurrency } from "@/lib/currency";

export function AdminOrderStatsWidget() {
  const navigate = useNavigate();
  const { data: stats, isLoading, error } = useOrderStatistics({});

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            Order Statistics
          </CardTitle>
          <CardDescription className="text-destructive-ink">
            Failed to load order statistics
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const hasPendingOrders = stats.pendingOrders > 0;
  const hasFailedOrders = stats.failedOrders > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" />
          Order Statistics
        </CardTitle>
        <CardDescription>Overview of system orders</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-4">
          {/* Total Orders */}
          <Card
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate("/admin/orders")}
          >
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">
                  {stats.totalOrders.toLocaleString()}
                </p>
                {stats.totalOrders > 0 && (
                  <div className="flex items-center text-xs text-muted-foreground">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    View all
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pending Orders */}
          <Card
            className={`cursor-pointer hover:bg-muted/50 transition-colors ${
              hasPendingOrders ? "border-warn/50" : ""
            }`}
            onClick={() => navigate("/admin/orders?status=Pending")}
          >
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <Clock className="h-4 w-4 text-warn-ink" />
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{stats.pendingOrders}</p>
                  {hasPendingOrders && (
                    <Badge variant="outline" className="bg-warn-soft text-warn-ink">
                      Action Needed
                    </Badge>
                  )}
                </div>
                {hasPendingOrders && (
                  <p className="text-xs text-muted-foreground">
                    Requires attention
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Total Revenue */}
          <Card
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate("/admin/orders?status=Complete")}
          >
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <DollarSign className="h-4 w-4 text-ok-ink" />
                </div>
                <p className="text-2xl font-bold">
                  {formatCurrency(stats.totalRevenue, stats.currency || "VND")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats.completedOrders} completed orders
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Failed Orders */}
          <Card
            className={`cursor-pointer hover:bg-muted/50 transition-colors ${
              hasFailedOrders ? "border-destructive/50" : ""
            }`}
            onClick={() => navigate("/admin/orders?status=Failed")}
          >
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Failed</p>
                  <AlertTriangle className="h-4 w-4 text-destructive-ink" />
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{stats.failedOrders}</p>
                  {hasFailedOrders && (
                    <Badge variant="destructive">
                      {stats.failedOrders > 10 ? "Critical" : "Review"}
                    </Badge>
                  )}
                </div>
                {hasFailedOrders ? (
                  <p className="text-xs text-destructive-ink">
                    Needs investigation
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    All orders successful
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
}
